/**
 * Low Stock Alert Dashboard Widget Component
 *
 * Displays inventory items that are low or out of stock.
 * Provides quick actions for ordering and adjusting stock.
 */

import { useState, useMemo } from 'react';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Chip,
  Badge,
  Divider,
  LinearProgress,
  Collapse,
  Alert,
  Tooltip,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import type { InventoryItem } from '@/types';
import { getStockStatus } from '@/lib/inventoryUtils';
import { useTranslation } from 'react-i18next';

interface LowStockAlertWidgetProps {
  items: InventoryItem[];
  onViewItem?: (item: InventoryItem) => void;
  onOrderItem?: (item: InventoryItem) => void;
  onCreateBulkOrder?: (items: InventoryItem[]) => void;
  onRefresh?: () => void;
  maxItemsToShow?: number;
  showOutOfStock?: boolean;
  showLowStock?: boolean;
}

type AlertLevel = 'critical' | 'warning';

interface AlertItem {
  item: InventoryItem;
  level: AlertLevel;
  shortage: number;
  daysUntilOut?: number;
}

export function LowStockAlertWidget({
  items,
  onViewItem,
  onOrderItem,
  onCreateBulkOrder,
  onRefresh,
  maxItemsToShow = 10,
  showOutOfStock = true,
  showLowStock = true,
}: LowStockAlertWidgetProps) {
  const { t } = useTranslation('inventory');
  const [expanded, setExpanded] = useState(true);

  const alertItems = useMemo(() => {
    const alerts: AlertItem[] = [];

    items.forEach((item) => {
      const status = getStockStatus(item);

      if (status === 'out_of_stock' && showOutOfStock) {
        alerts.push({
          item,
          level: 'critical',
          shortage: item.minStockLevel - item.quantity,
        });
      } else if (status === 'low_stock' && showLowStock) {
        alerts.push({
          item,
          level: 'warning',
          shortage: item.minStockLevel - item.quantity,
          // Estimate days until out of stock based on average usage
          daysUntilOut: Math.floor(item.quantity / 2), // Simplified calculation
        });
      }
    });

    // Sort: critical first, then by shortage amount
    alerts.sort((a, b) => {
      if (a.level !== b.level) {
        return a.level === 'critical' ? -1 : 1;
      }
      return b.shortage - a.shortage;
    });

    return alerts.slice(0, maxItemsToShow);
  }, [items, showOutOfStock, showLowStock, maxItemsToShow]);

  const criticalCount = alertItems.filter((a) => a.level === 'critical').length;
  const warningCount = alertItems.filter((a) => a.level === 'warning').length;

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const getAlertColor = (level: AlertLevel) => {
    return level === 'critical' ? 'error' : 'warning';
  };

  const getAlertIcon = (level: AlertLevel) => {
    return level === 'critical' ? (
      <ErrorIcon sx={{ fontSize: 20 }} />
    ) : (
      <WarningIcon sx={{ fontSize: 20 }} />
    );
  };

  const getStockPercentage = (item: InventoryItem) => {
    return Math.min(100, (item.quantity / item.minStockLevel) * 100);
  };

  if (alertItems.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingDownIcon color="action" />
            <Typography variant="h6" fontWeight={600}>
              {t('lowStockAlerts.title')}
            </Typography>
          </Box>
          {onRefresh && (
            <Tooltip title={t('lowStockAlerts.refresh')}>
              <IconButton size="small" onClick={onRefresh} aria-label={t('lowStockAlerts.refresh')}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Alert severity="success">
          {t('lowStockAlerts.allInStock')}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={handleToggleExpand}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Badge
            badgeContent={criticalCount + warningCount}
            color={criticalCount > 0 ? 'error' : 'warning'}
          >
            <TrendingDownIcon />
          </Badge>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {t('lowStockAlerts.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {criticalCount > 0 && `${criticalCount} ${t('lowStockAlerts.critical')}`}
              {criticalCount > 0 && warningCount > 0 && ', '}
              {warningCount > 0 && `${warningCount} ${t('lowStockAlerts.warning')}`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onRefresh && (
            <Tooltip title={t('lowStockAlerts.refresh')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
                aria-label={t('lowStockAlerts.refresh')}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={expanded ? t('lowStockAlerts.collapse') : t('lowStockAlerts.expand')}>
            <IconButton size="small" aria-label={expanded ? t('lowStockAlerts.collapse') : t('lowStockAlerts.expand')}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider />

        {/* Alert List */}
        <List disablePadding>
          {alertItems.map((alert, index) => (
            <Box key={alert.item.id}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  py: 1.5,
                  px: 2,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ color: `${getAlertColor(alert.level)}.main` }}>
                    {getAlertIcon(alert.level)}
                  </Box>
                </Box>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {alert.item.name}
                      </Typography>
                      <Chip
                        label={alert.item.partNumber}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('lowStockAlerts.stockInfo', { qty: alert.item.quantity, min: alert.item.minStockLevel })}
                        </Typography>
                        {alert.daysUntilOut !== undefined && (
                          <Chip
                            label={t('lowStockAlerts.daysRemaining', { days: alert.daysUntilOut })}
                            size="small"
                            color="warning"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getStockPercentage(alert.item)}
                        color={getAlertColor(alert.level)}
                        sx={{ height: 4, borderRadius: 1 }}
                      />
                      {alert.item.supplier && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {t('lowStockAlerts.supplierInfo', { name: alert.item.supplier.name, days: alert.item.supplier.deliveryDays })}
                        </Typography>
                      )}
                    </Box>
                  }
                />

                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {onOrderItem && (
                      <Tooltip title={t('createOrder')}>
                        <IconButton
                          size="small"
                          color={alert.level === 'critical' ? 'error' : 'warning'}
                          onClick={() => onOrderItem(alert.item)}
                          aria-label={t('createOrder')}
                        >
                          <ShoppingCartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onViewItem && (
                      <Tooltip title={t('view')}>
                        <IconButton
                          size="small"
                          onClick={() => onViewItem(alert.item)}
                          aria-label={t('view')}
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            </Box>
          ))}
        </List>

        {/* Footer Actions */}
        {onCreateBulkOrder && alertItems.length > 1 && (
          <>
            <Divider />
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {criticalCount > 0 && t('lowStockAlerts.criticalAttention', { count: criticalCount })}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<ShoppingCartIcon />}
                color={criticalCount > 0 ? 'error' : 'warning'}
                onClick={() => onCreateBulkOrder(alertItems.map((a) => a.item))}
              >
                {t('lowStockAlerts.orderAll', { count: alertItems.length })}
              </Button>
            </Box>
          </>
        )}
      </Collapse>
    </Paper>
  );
}
